import { createSlice } from '@reduxjs/toolkit';
import axios from '../../utils/axios';
import { dispatch } from 'redux/store';
import { DispatchError } from '../dispatchError';
import { ProviderSetupDTO, ProvidersState } from '../../@types/providers';
import { translator } from '../../translation/useTranslator';

const initialState: ProvidersState = {
  isLoading: {},
  providers: [],
  countryCodes: [],
  currentProviderSetup: {
    SMS: {
      providerId: '',
      defaultCountryCode: ''
    },
    Email: {
      providerId: ''
    }
  }
};

const setLoading = (state: ProvidersState, action: { payload: string }, isLoading: boolean) => {
  state.isLoading = { ...state.isLoading, [action.payload]: isLoading };
};

const slice = createSlice({
  name: 'providers',
  initialState,
  reducers: {
    startLoading(state, action) {
      setLoading(state, action, true);
    },
    stopLoading(state, action) {
      setLoading(state, action, false);
    },
    setProviders(state, action) {
      state.providers = action.payload;
    },
    setCurrentProviderSetup(state, action) {
      state.currentProviderSetup = action.payload;
    },
    setCountryCodes(state, action) {
      state.countryCodes = action.payload;
    }
  }
});

export default slice.reducer;

const handleApiCall = async (
  actionKey: string,
  apiCall: () => Promise<any>,
  successCallback: (data: any) => void,
  successMessage: string,
  errorMessage: string
) => {
  dispatch(slice.actions.startLoading(actionKey));
  try {
    const response = await apiCall();
    successCallback(response.data);
    dispatch(slice.actions.stopLoading(actionKey));
    return {
      result: response,
      defaultSuccessMessage: successMessage
    };
  } catch (error: any) {
    dispatch(slice.actions.stopLoading(actionKey));
    throw new DispatchError({
      error: error,
      defaultErrorMessage: errorMessage
    });
  }
};

export const getCountryCodes = () => {
  return async () => {
    return handleApiCall(
      'getCountryCodes',
      () => axios.get('/country-codes'),
      (data) => dispatch(slice.actions.setCountryCodes(data)),
      translator?.countryCodesFetched() ?? 'Country codes fetched',
      translator?.couldNotFetchCountryCodes() ?? 'Could not fetch country codes'
    );
  };
};

export const getProviders = () => {
  return async () => {
    return handleApiCall(
      'getProviders',
      () => axios.get('/providers'),
      (data) => dispatch(slice.actions.setProviders(data)),
      translator?.prvoidersFetched() ?? 'Providers fetched',
      translator?.couldNotFetchProviders() ?? 'Could not fetch providers'
    );
  };
};

type GetCurrentProviderSetupProps = {
  orgId: string;
};

export const getCurrentProviderSetup = ({ orgId }: GetCurrentProviderSetupProps) => {
  return async () => {
    return handleApiCall(
      'getCurrentProviderSetup',
      () => axios.get(`organizations/${orgId}/provider-setups`),
      (data) => dispatch(slice.actions.setCurrentProviderSetup(data)),
      translator?.prvoidersFetched() ?? 'Providers fetched',
      translator?.couldNotFetchProviders() ?? 'Could not fetch providers'
    );
  };
};

type UpdateCurrentProviderSetupProps = {
  orgId: string;
  providerSetup: ProviderSetupDTO;
};

export const updateCurrentProviderSetup = ({
  orgId,
  providerSetup
}: UpdateCurrentProviderSetupProps) => {
  return async () => {
    return handleApiCall(
      'updateCurrentProviderSetup',
      () => axios.put(`organizations/${orgId}/provider-setups`, providerSetup),
      (data) => dispatch(slice.actions.setCurrentProviderSetup(data)),
      translator?.providerUpdated() ?? 'Provider updated',
      translator?.couldNotUpdateProvider() ?? 'Could not update provider'
    );
  };
};
